import {
  Action,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';

import {
  CompanyViewModel,
  UserAccessAction,
  UserProfileModel,
} from '../../models';
import { oidcActions } from '../oidc';
import * as userActions from './user.actions';

export interface State {
  userProfile: UserProfileModel | null;
  userAccessActions: UserAccessAction[];
  activeCompany: CompanyViewModel | null;
  hasManyCompanies: boolean;
  companyDataIsReady: boolean;
  lastError: any;
}

export const initialState: State = {
  userProfile: null,
  userAccessActions: [],
  activeCompany: null,
  hasManyCompanies: false,
  companyDataIsReady: false,
  lastError: null,
};

const userReducers = createReducer(
  initialState,
  on(userActions.init, (state) => ({
    ...state,
    companyDataIsReady: false,
  })),
  on(userActions.logIn, (state) => ({
    ...state,
    companyDataIsReady: false,
  })),
  on(userActions.logOut, (state) => ({
    ...state,
    userProfile: null,
    userAccessActions: [],
    activeCompany: null,
    companyDataIsReady: false,
  })),
  on(oidcActions.checkAuth, (state) => ({
    ...state,
    userProfile: null,
    userAccessActions: [],
    activeCompany: null,
    hasManyCompanies: false,
  })),
  on(userActions.setHasManyCompanies, (state, action) => ({
    ...state,
    hasManyCompanies: action.hasManyCompanies,
  })),
  on(userActions.setActiveCompany, (state, action) => ({
    ...state,
    userProfile: null,
    userAccessActions: [],
    activeCompany: { ...action.company } as CompanyViewModel,
    companyDataIsReady: true,
  })),
  on(userActions.setUserProfile, (state, action) => ({
    ...state,
    userProfile: ((action.userApiProfile &&
      new UserProfileModel(action.userApiProfile, action.forceLanguage)) ||
      null) as UserProfileModel,
  })),
  on(userActions.userProfileLanguageChanged, (state, action) => {
    const userLanguage =
      action.userLanguage && action.userLanguage.toLocaleLowerCase();
    const userProfileLanguage =
      state.userProfile && state.userProfile.userLanguage.toLocaleLowerCase();

    if (userLanguage === userProfileLanguage) {
      return state;
    }

    return {
      ...state,
      userProfile: {
        ...state.userProfile,
        userLanguage,
      } as UserProfileModel,
    };
  }),

  on(oidcActions.userDataReceived, (state, action) => ({
    ...state,
    companyDataIsReady: false,
  })),
  on(userActions.setUserAccessActions, (state, action) => ({
    ...state,
    userAccessActions: [
      ...state.userAccessActions,
      ...Object.keys(action.accessActions).map((accessAction) => ({
        action: accessAction,
        allow: action.accessActions[accessAction],
        timestamp: new Date(),
      })),
    ],
  })),
  on(userActions.setActiveCompanyFailed, (state, action) => ({
    ...state,
    lastError: action.err,
  }))
);

export function reducer(state: State | undefined, action: Action): State {
  return userReducers(state, action);
}

export const userReducer = reducer;
export const userFeatureKey = 'core-user';

export const userState = createFeatureSelector<State>(userFeatureKey);

export const getUserProfile = createSelector(
  userState,
  // Need to check state existence because
  // selector could be called before state
  // setup in monitor.service.ts
  (state) => state && state.userProfile
);

export const hasAccess = createSelector(
  userState,
  (state) => state.userProfile && state.userProfile.hasAccessToModule
);

export const getUserLanguage = createSelector(
  userState,
  (state) => state.userProfile && state.userProfile.userLanguage
);

export const getActiveCompany = createSelector(
  userState,
  (state) => state.activeCompany
);

export const hasManyCompanies = createSelector(
  userState,
  (state) => state.hasManyCompanies
);

export const companyDataIsReady = createSelector(
  userState,
  (state) => state.companyDataIsReady
);

export const userAccessActions = createSelector(
  userState,
  (state) => state.userAccessActions
);

export const lastError = createSelector(userState, (state) => state.lastError);
